import { Grid, Paper, Typography, Box, Button, CircularProgress } from "@material-ui/core";
import PageTitle from "components/PageTitle/PageTitle";
import { useEffect, useState } from "react";
import { dbGet, dbPost, dbPostWithFormData } from "utils/DBFetchers";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import PopupMessage from "components/PopupMessage/PopupMessage";

import useStyles from "./styles";

import PharmacySelector from "components/CustomFields/PharmacySelector";


export default function PharmacyBalancesPage() {
  const classes = useStyles();
  const [balance, setBalance] = useState(0);
  const [debitValue, setDebitValue] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();
  const [showConfirmationPopUp, setShowConfirmationPopUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRut, setSelectedRut] = useState("");
  const [downloading, setDownloading] = useState(false);

  const onChangeSelectedRut = (rut) => {
    setSelectedRut(rut);
    setDebitValue("");
    setDescripcion("");
  };


  const loadPharmacyBalance = (pharmacyRut) => {
    if (!pharmacyRut) {
      setBalance(0);
      return;
    }
    dbGet(`wallet/internal/balances/pharmacies/${pharmacyRut}`)
      .then((data) => {
        setBalance(data.balance);
      })
      .catch((error) => {
        setBalance(0);
        snackbarOpen("error", "Error de comunicación con el servidor");
      });
  }

  useEffect(() => {
    loadPharmacyBalance(selectedRut);
  }, [selectedRut]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const formData = new FormData();
      formData.append("pharmacy_rut", selectedRut);

      const postData = await dbPostWithFormData(
        "cli/export_pharmacy_balance_composition",
        formData
      );
      if (!postData?.output_files) {
        throw new Error("Respuesta inválida del servidor");
      }

      const entries = Object.entries(postData.output_files || {});
      const [filename, url] = entries[0] || [];

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      snackbarOpen("success", "Archivo generado con éxito");
    } catch (e) {
      snackbarOpen("error", "Error al generar el archivo. Intente nuevamente.");
    } finally {
      setDownloading(false);
    }
  };

  const onConfirm = () => {
    setShowConfirmationPopUp(false);
  };

  const onReconfirm = () => {
    if (submitting) return;
    setSubmitting(true);

    const payload = {
      value: Number(debitValue),
      description: descripcion.trim(),
    }
    dbPost(`wallet/internal/laboratory_transactions/pharmacies/${selectedRut}`, payload)
      .then((data) => {
        loadPharmacyBalance(selectedRut)
        setDebitValue("");
        setDescripcion("");
        snackbarOpen("success", "Débito registrado con éxito");
      })
      .catch((error) => {
        const error_key = error?.errors?.key?.[0] || "";
        snackbarOpen("error", error_key || "Error al registrar el débito");
      })
      .finally(() => {
        setSubmitting(false);
        setShowConfirmationPopUp(false);
      });
  };

  return (
    <Grid>
      <PopupMessage
        title={"¿Confirma la operación?"}
        message={"Se registrará un débito por $" + (debitValue ? new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(debitValue) : "0.00")}
        open={showConfirmationPopUp}
        handleClose={onConfirm}
        executeButtonRight={
          <Button
            onClick={onReconfirm}
            variant="contained"
            color="secondary"
            className={classes.buttonSpacing}
            disabled={submitting}
            aria-busy={submitting}
            style={{ minWidth: 140 }} // evita “salto” de layout
          >
            {submitting ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <CircularProgress size={18} color="inherit" />
                Procesando...
              </span>
            ) : (
              "Confirmar"
            )}
          </Button>
        }
        contentStyle={{ maxWidth: 500 }}
        buttonDivClassName={classes.popupDivCenter}
        buttonClassName={classes.buttonSpacing}
      />
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />
      <PageTitle title="Billetera virtual - Saldo de Farmacias" />
      <Paper elevation={3} className={classes.paperContainer}>
        {/* Contenedor de filas con spacing (v4) */}
        <Grid container direction="column" spacing={3} style={{ padding: 16, background: "#f5f5f5" }}>

          {/* Fila 1: Farmacia + Saldo actual */}
          <Grid item>
            <Grid container alignItems="center" justify="space-between" spacing={2}>

              {/* Columna izquierda: farmacia */}
              <Grid item xs={12} sm>
                <div style={{ maxWidth: 560 }}>
                  <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                    Farmacia con billetera
                  </Typography>
                  <PharmacySelector
                    fieldClassName={classes.pharmacySelectionField}
                    value={selectedRut}                  // string con el RUT seleccionado
                    onChange={onChangeSelectedRut}            // te devolverá el rut (string) o "" si se limpia
                    program={1}
                    only_with_wallet={true}
                  />
                </div>
              </Grid>

              {/* Columna derecha: saldo */}
              <Grid item>
                <div style={{ textAlign: "center" }}>
                  <Typography style={{ fontWeight: 700, marginBottom: 8 }}>Saldo actual</Typography>
                  <Paper elevation={2} style={{ borderRadius: 12, background: "#f9f9f9", minWidth: 200, padding: "8px 16px" }}>
                    {(
                      <Typography variant="h5" style={{ fontWeight: 700, color: "#2e7d32" }}>
                        {selectedRut ? (
                          <>
                            ${new Intl.NumberFormat("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }).format(balance)}
                          </>
                        ) : (
                          <>-</>
                        )}
                      </Typography>
                    )}
                  </Paper>
                </div>
              </Grid>

            </Grid>
          </Grid>



          {/* Botón descarga de composición de saldos (alineado con saldo, derecha) */}
          <Grid item>
            <Grid container justify="flex-end">
              <Grid item>
                <Box display="flex" justifyContent="flex-end" p={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDownload}
                    disabled={downloading || !selectedRut}
                    startIcon={downloading ? <CircularProgress size={18} /> : null}
                  >
                    {downloading ? "Generando..." : "Descargar composición de Saldo"}
                  </Button>
                </Box>

              </Grid>
            </Grid>
          </Grid>

        </Grid>
      </Paper>
    </Grid>

  );
}
